import React, { useRef, useEffect, useState, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { API_KEYS_PATH, TEST_LINKED_ACCOUNTS_PATH } from "../../../../../router/RouterUtils";
import APIExplorerMetadata from "./APIExplorerMetadata";
import {
  APICategory,
  LinkedAccount,
  LinkedAccountStatus,
  UnreleasedAPICategory,
  UserType,
} from "../../../../../models/Entities";
import { FormErrorData, fetchWithAuth } from "../../../../../api-client/APIClient";
import { EndpointModelInfo, OptionConfig } from "../../types";
import { CategoryPrimaryModelInfo } from "../../constants";
import {
  Button,
  ButtonVariant,
  Card,
  CodeSnippet,
  Text,
  TextFieldVariant,
  Typeahead,
} from "@merge-api/merge-javascript-shared";
import { showErrorToast } from "../../../../shared/Toasts";
import LinkedAccountTypeaheadRenderOption from "../../../../LinkedAccountTypeaheadRenderOption";
import { formatDate } from "../../../../../models/Helpers";
import useAppContext from "../../../../context/useAppContext";

const optionConfigs: Array<OptionConfig<APICategory>> = (
  Object.keys(APICategory) as APICategory[]
).map((category) => ({
  key: category,
  displayEndpoint: CategoryPrimaryModelInfo[category].displayEndpointPath,
  testEndpoint: CategoryPrimaryModelInfo[category].testEndpointPath,
}));

/**
 * Interface used to parse APIKeyData
 */
interface APIKey {
  id: string;
  key_type: string;
  key: string;
  name: string | null;
  is_test: boolean;
  created_at: string;
}

interface APIKeyData {
  production_key?: APIKey | null;
  sandbox_key?: APIKey | null;
  remote_keys?: APIKey[];
}

type LinkedAccountOption = { type: "account"; account: LinkedAccount } | { type: "sample" };

// MergeBaseURL
const MergeBaseURL = process.env.REACT_APP_BASE_API_URL || "https://api.merge.dev";

const UnifiedAPIExplorer = () => {
  const { user } = useAppContext();
  const isMemberRole = user.type === UserType.member;
  const [testEndpoint, setTestEndpoint] = useState(
    CategoryPrimaryModelInfo[APICategory.hris].testEndpointPath,
  );
  const [apiKeyData, setAPIKeyData] = useState<APIKeyData | undefined>();
  const [remoteKey, setRemoteKey] = useState<APIKey | null>(null);
  const [responseBody, setResponseBody] = useState<string>("");
  const [responseSuccess, setResponseSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [linkedAccount, setLinkedAccount] = useState<LinkedAccount | undefined>();
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [linkedAccountAT, setLinkedAccountAT] = useState("");
  const [linkedAccountID, setLinkedAccountID] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<APICategory | UnreleasedAPICategory>(
    APICategory.hris,
  );
  const [isTestLinkedAccountSelected, setIsTestLinkedAccountSelected] = useState(true);
  const [apiOptionsConfig, setApiOptionsConfig] =
    useState<Record<APICategory | UnreleasedAPICategory, EndpointModelInfo[]>>();
  const [displayEndpoint, setDisplayEndpoint] = useState<string>("");

  const codeBlockRef1 = useRef<HTMLInputElement>(null);
  const codeBlockRef2 = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLInputElement>(null);

  const linkedAccountOptions: LinkedAccountOption[] = useMemo(
    () => [
      ...linkedAccounts.map((la) => ({ type: "account" as const, account: la })),
      { type: "sample" as const },
    ],
    [linkedAccounts],
  );

  const selectedLinkedAccountOption: LinkedAccountOption | null = useMemo(() => {
    if (isTestLinkedAccountSelected && linkedAccount) {
      return { type: "account", account: linkedAccount };
    }
    if (!isTestLinkedAccountSelected) {
      return { type: "sample" };
    }
    return null;
  }, [isTestLinkedAccountSelected, linkedAccount]);

  const updateWidth = () => {
    if (codeBlockRef1.current && containerRef.current && codeBlockRef2.current) {
      const refWidth = containerRef.current.getBoundingClientRect().width;
      codeBlockRef1.current.style.maxWidth = `${refWidth}px`;
      codeBlockRef2.current.style.maxWidth = `${refWidth}px`;
    }
  };

  const stripPathPrefix = (path: string) => {
    const path_array = path.split("/");
    const commonModelArray = path_array.splice(2);
    const commonModelPath = commonModelArray.join("/");
    return "/" + commonModelPath;
  };

  useEffect(() => {
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [containerRef]);

  useEffect(() => {
    fetchWithAuth({
      path: "/integrations/keys",
      method: "GET",
      onResponse: (data) => {
        setAPIKeyData(data);
        const testKeys = data.remote_keys.filter(
          (remote_key: { is_test: any }) => remote_key.is_test,
        );
        setRemoteKey(testKeys[0]);
      },
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);

    fetchWithAuth({
      path: `organizations/test-data/categories-and-models-info`,
      method: "GET",
      onResponse: (data: any) => {
        if (!data) {
          showErrorToast("An unkown error has occurred. Please try again later.");
        } else {
          setApiOptionsConfig(data);
        }
        setIsLoading(false);
      },
      onError: (err: Response | undefined) => {
        if (err) {
          err.json().then((data: FormErrorData) => {
            let wasToastShown = false;
            for (const field_name in data) {
              if (field_name === "non_field_errors") {
                showErrorToast(data[field_name][0]);
                wasToastShown = true;
              }
            }
            if (!wasToastShown) {
              showErrorToast("An unknown error has occurred. Please try again later.");
            }
          });
        } else {
          showErrorToast("A network error has occurred. Please try again.");
        }
        setIsLoading(false);
      },
    });
  }, []);

  useEffect(() => {
    setLinkedAccounts([]);
    fetchWithAuth({
      path: `/integrations/linked-accounts?status=${LinkedAccountStatus.COMPLETE}&is_test_account=true&page_size=100`,
      method: "GET",
      onResponse: (data) => {
        // For Member roles, we don't need to fetch or store the linked accounts
        // as they will only see the Sample test Linked Account option
        if (!isMemberRole) {
          setLinkedAccounts((linkedAccounts) => [...data.results, ...linkedAccounts]);
        }

        const firstLinkedAccount = data.results?.[0];
        if (firstLinkedAccount == null || isMemberRole) {
          /**
           * If there are no test Linked Accounts linked (e.g the user skips step one), we'll default to one of the dummy test Linked Accounts token
           * Lee made. That ID is below, and we'll set it as the default LinkedAccountID
           *
           * For Member roles, we always use the Sample test Linked Account
           */
          setLinkedAccountAT("SAMPLE TEST LINKED ACCOUNT TOKEN");
          setLinkedAccountID("ec1270eb-5345-d871-6669-80e932ce802a");
          setIsTestLinkedAccountSelected(false);
        } else {
          setLinkedAccount(firstLinkedAccount);
          const firstLA: LinkedAccount = firstLinkedAccount;

          const commonModelPath = stripPathPrefix(
            CategoryPrimaryModelInfo[firstLA.category].displayEndpointPath,
          );
          setDisplayEndpoint(commonModelPath);

          setTestEndpoint(CategoryPrimaryModelInfo[firstLA.category].testEndpointPath);
          setIsTestLinkedAccountSelected(true);
        }
      },
    });
  }, [isMemberRole]);

  /*
   ** Render function for all new Linked Accounts
   */

  useEffect(() => {
    if (linkedAccount && isTestLinkedAccountSelected) {
      setLinkedAccountAT(linkedAccount.account_token);
      setLinkedAccountID(linkedAccount.id);
      setSelectedCategory(linkedAccount.category);
      setTestEndpoint(CategoryPrimaryModelInfo[linkedAccount.category]?.testEndpointPath);
    }
  }, [linkedAccount, isTestLinkedAccountSelected]);

  /**
   * Sends a GET request using the selected test Linked Accounts Auth & X-Account-Token
   * setTimeout is used to show the spinner and give the user some feedback to improve the experience. Our API returns data too quickly
   */
  const onSubmit = () => {
    setIsLoading(true);
    fetchWithAuth({
      path: `/organizations/test-data/${testEndpoint}/${linkedAccountID}`,
      method: "GET",
      onResponse: (data) => {
        setTimeout(() => {
          setResponseSuccess(true);
          setIsLoading(false);
          setResponseBody(JSON.stringify(data, null, 2));
        }, 200);
      },
      onError: () => {
        showErrorToast(
          "An error occurred while trying to perform this API request. Please try again.",
        );
        setIsLoading(false);
      },
    });
  };

  // Dynamic display text used in Request codeblock below
  const displayText = `curl ${MergeBaseURL}/api/${
    isTestLinkedAccountSelected && displayEndpoint
      ? selectedCategory + "/v1" + displayEndpoint
      : CategoryPrimaryModelInfo[selectedCategory]?.displayEndpointPath
  } \\
-H "Authorization: Bearer ${remoteKey?.id}" \\
-H "X-ACCOUNT-TOKEN: ${linkedAccountAT}"`;

  return (
    <div className="flex-1">
      <Card className="rounded-xl p-8 flex flex-col max-w-full mt-8 mb-[60px]">
        <Text variant="h3" className="mb-8">
          Unified API explorer
        </Text>
        <Card variant="outline" className="flex flex-col">
          <div className="p-6 flex flex-col gap-y-3 border-b border-gray-10">
            <div className="flex flex-row items-center justify-between">
              <Text variant="h6">Test Linked Account</Text>
              <Button
                variant={ButtonVariant.TextBlue}
                onClick={() => window.open(TEST_LINKED_ACCOUNTS_PATH, "_blank")}
                rightIcon={<ChevronRight size={12} />}
                size="sm"
              >
                Test Linked Accounts
              </Button>
            </div>
            <Typeahead
              options={linkedAccountOptions}
              value={selectedLinkedAccountOption}
              onChange={(_: any, value: LinkedAccountOption | null) => {
                if (!value || value.type === "sample") {
                  setLinkedAccountAT("SAMPLE TEST LINKED ACCOUNT TOKEN");
                  setLinkedAccountID("ec1270eb-5345-d871-6669-80e932ce802a");
                  setDisplayEndpoint(optionConfigs[0].displayEndpoint);
                  setTestEndpoint(optionConfigs[0].testEndpoint);
                  setIsTestLinkedAccountSelected(false);
                } else {
                  setLinkedAccount(value.account);
                  if (apiOptionsConfig) {
                    const commonModelPath = stripPathPrefix(
                      apiOptionsConfig[value.account.category][0].displayEndpointPath,
                    );
                    setDisplayEndpoint(commonModelPath);
                  }
                  setTestEndpoint(
                    apiOptionsConfig
                      ? apiOptionsConfig[value.account.category][0].testEndpointPath
                      : "",
                  );
                  setIsTestLinkedAccountSelected(true);
                }
              }}
              getOptionLabel={(option: LinkedAccountOption) =>
                option.type === "account"
                  ? option.account.end_user.organization_name
                  : "Sample test Linked Account"
              }
              getOptionKey={(option: LinkedAccountOption) =>
                option.type === "account" ? option.account.id : "sample"
              }
              areOptionsEqual={(a: LinkedAccountOption, b: LinkedAccountOption) => {
                if (a.type === "sample" && b.type === "sample") return true;
                if (a.type === "account" && b.type === "account")
                  return a.account.id === b.account.id;
                return false;
              }}
              renderOption={(option: LinkedAccountOption) =>
                option.type === "account" ? (
                  <LinkedAccountTypeaheadRenderOption linkedAccount={option.account} />
                ) : (
                  <div className="flex flex-row items-center">
                    Sample test Linked Account
                    <span className="ml-2 text-sm font-semibold badge bg-secondary-soft">
                      Sample
                    </span>
                  </div>
                )
              }
              placeholder="Select Linked Account..."
              disableClearable
            />
            <APIExplorerMetadata
              linkedAccount={isTestLinkedAccountSelected ? linkedAccount : undefined}
            />
          </div>
          <div className="p-6 flex flex-col gap-y-3 border-b border-gray-10">
            <div className="flex flex-row items-center justify-between">
              <Text variant="h6">Test API key</Text>
              <Button
                variant={ButtonVariant.TextBlue}
                onClick={() => window.open(API_KEYS_PATH, "_blank")}
                rightIcon={<ChevronRight size={12} />}
                size="sm"
              >
                API keys
              </Button>
            </div>

            <Typeahead
              options={apiKeyData?.remote_keys?.filter((remote_key) => remote_key.is_test) ?? []}
              onChange={(_, value: APIKey | null) => {
                setRemoteKey(value);
              }}
              placeholder="Select..."
              value={remoteKey}
              getOptionLabel={(option: APIKey) => option.name ?? ""}
              renderOption={(option: APIKey) => (
                <div className="flex flex-row justify-between items-center w-full gap-2">
                  <Text>{option.name}</Text>
                  <Text variant="sm" className="text-gray-40">
                    Created {formatDate(option.created_at, "MMM DD, YYYY")}
                  </Text>
                </div>
              )}
            />
          </div>
          <div className="p-6 flex flex-col gap-y-3">
            <Text variant="h6">Endpoint</Text>
            <div className="m-0 d-flex flex-row">
              {isTestLinkedAccountSelected ? (
                <>
                  <div className="w-full max-w-fit rounded-l-md px-3 flex items-center bg-gray-0 whitespace-nowrap truncate border border-gray-20">
                    <Text variant="md-mono" className="text-gray-40">
                      GET {MergeBaseURL}/api/{linkedAccount?.category}/v1{" "}
                    </Text>
                  </div>
                  <div className="grow [&_div]:!rounded-l-none [&_input]:!rounded-l-none [&_div]:!border-l-0 [&_input]:!border-l-0">
                    <Typeahead
                      borderVariant={TextFieldVariant.Bordered}
                      options={
                        linkedAccount?.category && apiOptionsConfig
                          ? apiOptionsConfig[linkedAccount.category]
                          : []
                      }
                      value={
                        linkedAccount?.category && apiOptionsConfig
                          ? apiOptionsConfig[linkedAccount.category].find(
                              (opt) => stripPathPrefix(opt.displayEndpointPath) === displayEndpoint,
                            ) ?? null
                          : null
                      }
                      onChange={(_, value: EndpointModelInfo | null) => {
                        if (value) {
                          setTestEndpoint(value.testEndpointPath);
                          setDisplayEndpoint(stripPathPrefix(value.displayEndpointPath));
                          setSelectedCategory(linkedAccount!.category);
                        }
                      }}
                      getOptionLabel={(option: EndpointModelInfo) =>
                        stripPathPrefix(option.displayEndpointPath)
                      }
                      getOptionKey={(option: EndpointModelInfo) => option.displayEndpointPath}
                      placeholder="Select endpoint..."
                      disableClearable
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="w-full max-w-fit font-mono font-normal text-[13px] leading-5 rounded-l-md text-gray-40 px-3 flex items-center bg-gray-0 whitespace-nowrap truncate border border-gray-10">
                    GET {MergeBaseURL}/api/
                  </div>
                  <div className="grow [&_div]:!rounded-l-none [&_input]:!rounded-l-none [&_div]:!border-l-0 [&_input]:!border-l-0">
                    <Typeahead
                      borderVariant={TextFieldVariant.Bordered}
                      options={optionConfigs}
                      value={optionConfigs.find((opt) => opt.key === selectedCategory) ?? null}
                      onChange={(_, value: OptionConfig<APICategory> | null) => {
                        if (value) {
                          setTestEndpoint(value.testEndpoint);
                          setSelectedCategory(value.key);
                        }
                      }}
                      getOptionLabel={(option: OptionConfig<APICategory>) => option.displayEndpoint}
                      getOptionKey={(option: OptionConfig<APICategory>) => option.key}
                      placeholder="Select endpoint..."
                      disableClearable
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>

        <Button onClick={onSubmit} loading={isLoading} fullWidth className="my-5">
          Send API request
        </Button>

        {/* Request code block embeds API key id and account token; Response carries live customer payload; both masked at the block (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot) */}
        <div ref={codeBlockRef1} className="ph-no-capture" data-fr-ignore>
          <CodeSnippet
            className="mb-9"
            title="Request"
            language="bash"
            variant="gray"
            showCopyButton
            maxHeight="max-h-[150px]"
            textToCopy={displayText}
          >
            {displayText}
          </CodeSnippet>
        </div>
        <div ref={codeBlockRef2} className="ph-no-capture" data-fr-ignore>
          <CodeSnippet
            title="Response"
            language="json"
            variant="gray"
            showCopyButton
            textToCopy={responseBody}
          >
            {responseSuccess
              ? responseBody
              : `Press "Send API request"
            `}
          </CodeSnippet>
        </div>
      </Card>
    </div>
  );
};

export default UnifiedAPIExplorer;
